import { Text, Button, Icon, IconButton, Popover, Tooltip } from "@oysterhr/ui"

import { useState } from "react"
import * as React from "react"
import classNames from "classnames"
import { OysterIcon } from "@oysterhr/icons"

interface BaseDropdownValue {
  title: React.ReactNode
  itemStyle?: string
  icon?: OysterIcon
  danger?: boolean
  disabled?: boolean
}

interface ButtonDropdownValue extends BaseDropdownValue {
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  dataTestid?: string
  dataAnalyticsName?: string
  href?: never
  target?: never
  rel?: never
}

interface LinkDropdownValue extends BaseDropdownValue {
  onClick?: never
  dataTestid?: string
  dataAnalyticsName?: string
  href?: string
  target?: string
  rel?: string
}

export type DropdownValue = ButtonDropdownValue | LinkDropdownValue

interface Props {
  title?: string
  dropdownValues: DropdownValue[]
  lockedMessage?: string
  itemStyle?: string
  containerStyle?: string
  buttonSize?: "sm" | "md"
  fullWidth?: boolean
  /**
   * Useful for accessibility in instances where the "title" prop is not provided
   * The getByRole method of React Testing Library also uses it to query elements by name (https://stackoverflow.com/a/65076656)
   */
  ariaLabel?: string
  placement?: React.ComponentProps<typeof Popover>["placement"]
  buttonVariation?: "outline" | "borderless"
}

export const DotDropdown = ({
  dropdownValues = [],
  lockedMessage,
  itemStyle,
  containerStyle,
  title,
  buttonSize = "md",
  fullWidth,
  ariaLabel,
  placement = "bottom-start",
  buttonVariation = "outline",
}: Props) => {
  const [show, setShow] = useState(false)
  const locked = !!lockedMessage

  const isMounted = React.useRef(false)

  React.useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  })

  const buttonProps = {
    "aria-disabled": !!locked || undefined,
    "data-testid": "edit-button",
    size: buttonSize,
    variation: buttonVariation,
  }

  const icon = locked ? "Lock" : "DotsThreeVertical"

  const _button = title ? (
    <Button {...buttonProps} isFullWidth={fullWidth} startAdornment={<Icon name={icon} />}>
      {title}
    </Button>
  ) : (
    <IconButton {...buttonProps} aria-label={ariaLabel} iconName={icon} />
  )

  return (
    <div data-testid="dot-dropdown">
      <Tooltip value={lockedMessage ?? null}>
        <div>
          <Popover open={show} onOpenChange={open => setShow(open)} placement={placement}>
            <Popover.Trigger
              onClick={e => {
                e.stopPropagation()

                if (!locked) {
                  setShow(prev => !prev)
                }
              }}
            >
              {_button}
            </Popover.Trigger>
            <Popover.Content data-testid="dot-dropdown-menu">
              <div
                className={classNames(
                  "flex min-w-40 flex-col rounded bg-white p-2 text-left text-base font-normal leading-4 text-gray-400",
                  {
                    [containerStyle ?? ""]: containerStyle,
                    "w-full": fullWidth,
                  },
                )}
                style={{ boxShadow: "0 1px 10px 1px rgba(48, 78, 118, 0.08)" }}
                data-testid="dot-dropdown-menu"
              >
                {dropdownValues.map((value, idx) => (
                  <a
                    className={classNames("p-2 ", itemStyle, value.itemStyle, {
                      "text-error-base": value.danger,
                      "cursor-pointer hover:bg-gray-100": !value.disabled,
                      "cursor-not-allowed": value.disabled,
                    })}
                    data-testid={value.dataTestid}
                    data-analytics-name={value.dataAnalyticsName}
                    key={`${value.title}${idx}`}
                    role={value.onClick ? "button" : undefined}
                    onClick={async e => {
                      // FIXME: This is just wrong. Buttons should never be anchors, and there's no use-case where this was needed.
                      // Unfortunately I don't think changing this in this PR would be easy, so I'm hacking around it.
                      // In the future, we should just remove `href` from the DropdownValues, and make sure all the usages
                      // of this component are updated to use `onClick` instead.
                      if (!value.href) {
                        e.preventDefault()
                      }
                      e.stopPropagation()
                      // If this action is disabled, don't do anything.
                      if (value.disabled) {
                        return
                      }
                      if (value.onClick) {
                        await value.onClick(e)
                      }

                      if (isMounted.current) setShow(false)
                    }}
                    href={value.href ?? "#"}
                    target={value.target}
                    rel={value.rel}
                  >
                    {value.icon && <Icon className="mr-1" name={value.icon} />}
                    <Text
                      className={classNames("text-sm", {
                        "text-gray-400": !value.disabled && !value.danger,
                        "text-gray-300": value.disabled,
                      })}
                    >
                      {value.title}
                    </Text>
                  </a>
                ))}
              </div>
            </Popover.Content>
          </Popover>
        </div>
      </Tooltip>
    </div>
  )
}

export default DotDropdown
